import React, { useEffect, useRef, useLayoutEffect, useState  } from 'react';
import spogenLogo from '#/assets/spogen_logo_no_arcs.svg';
import valtraLogo from '#/assets/valtra_logo_red_lines_2.svg';
import heroImage from '#/assets/qseries_tt.png';
import valtraCoach from '#/assets/valtra_coach.svg';
import qrCode from '#/assets/qr-code_valtra.svg';
import { sendHumanDetectionImage } from '#/api/vision';
import welcomeAudioEn from '#/assets/sounds/start_screen_en.mp3';
import welcomeAudioDe from '#/assets/sounds/start_screen_de.mp3';
import { Language } from '#/types/preferences';
import { updateSettings } from '#/api/settings';
import { useTranslation } from 'react-i18next';
import { useAudioPlayback } from '#/contexts/PlaybackContext';

import enFlag from '#/assets/flags/uk_flag.svg';
import finlandFlag from '#/assets/flags/finland_flag.svg';
import franceFlag from '#/assets/flags/france_flag.svg';
import germanyFlag from '#/assets/flags/germany_flag.svg';

import styles from './start-screen.module.css';
import logger from "#/utils/logger";


declare global {
  interface Window { __appAC?: AudioContext; }
}

interface Props {
  onStart: (lang: Language) => void;
  refetchSession: (lang: Language) => Promise<void>;
  visible: boolean;
}


const languages = [
  { code: Language.DE, label: 'Deutsch',  flag: germanyFlag },
  { code: Language.EN, label: 'English', flag: enFlag },
  { code: Language.FR, label: 'Français',  flag: franceFlag },
  { code: Language.FI, label: 'Suomi', flag: finlandFlag },
];


const StartScreen: React.FC<Props> = ({ onStart,
                                        refetchSession,
                                        visible, }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const startingRef = useRef(false);                 // prevent overlapping starts
  const watchdogRef = useRef<number | undefined>(undefined);
  const hasWelcomedRef = useRef(false);
  const detectIntervalRef = useRef<number | undefined>(undefined);
  const startDelayRef = useRef<number | undefined>(undefined);
  const [selectedLang, setSelectedLang] = useState<Language>(Language.EN);
  const welcomeAudioDeRef = useRef<HTMLAudioElement | null>(null);
  const welcomeAudioEnRef = useRef<HTMLAudioElement | null>(null);
  const { t, i18n } = useTranslation();
  const { setAudioUnlocked } = useAudioPlayback();
  const hasPrimedRef = useRef(false);
  const isWelcomePlayingRef = useRef(false);
  const runTokenRef = useRef(0);                 // increments on every (re)start/hide
  const detectAbortRef = useRef<AbortController | null>(null);
  const visibleRef = useRef(visible);

  useEffect(() => { visibleRef.current = visible; }, [visible]);

  // Put this inside your component, above startCameraAndDetection
  const waitForRealFrames = (vid: HTMLVideoElement, timeoutMs = 1500) =>
    new Promise<boolean>((resolve) => {
      if (!vid) return resolve(false);

      let rafId = 0;
      const start = Date.now();

      const finish = (ok: boolean) => {
        if (rafId) cancelAnimationFrame(rafId);
        resolve(ok);
      };

      const check = () => {
        // iOS sometimes reports readyState but keeps dimensions at 0
        if (vid.videoWidth > 0 && vid.videoHeight > 0) return finish(true);
        if (Date.now() - start >= timeoutMs) return finish(false);
        rafId = requestAnimationFrame(check);
      };

      if (vid.readyState >= 1) {
        check();
      } else {
        const onMeta = () => {
          vid.removeEventListener('loadedmetadata', onMeta);
          check();
        };
        vid.addEventListener('loadedmetadata', onMeta, { once: true });
        // safety: if metadata never fires, still poll
        rafId = requestAnimationFrame(check);
      }
    });

    const startCameraAndDetection = async () => {
      if (startingRef.current) return;          // Prevent re-entrancy
      startingRef.current = true;
      try {
        await stopStream();

        // 1) gUM with fallback (prevents black stream on some iPads)
        let stream: MediaStream | null = null;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: {
              facingMode: { ideal: 'user' },
              width:  { ideal: 1280 },
              height: { ideal: 960 },
            },
          });
        } catch {
          stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: true });
        }

        const vid = videoRef.current;
        if (!vid || !stream) throw new Error('No video element or stream');

        // 2) iOS attributes + bind stream
        vid.muted = true;
        vid.setAttribute('muted', '');
        vid.setAttribute('playsinline', '');
        (vid as any).webkitPlaysInline = true;
        vid.autoplay = true;
        vid.srcObject = stream;

        // 3) Try to play (twice is fine on iOS)
        try { await vid.play(); } catch {}
        if (vid.paused) vid.play().catch(() => {});

        // 4) Wait for *real* frames (not just metadata). If none -> restart with fallback.
        const gotFrames = await waitForRealFrames(vid, 1500);
        if (!gotFrames) {
          logger.warn('[StartScreen] No real frames within timeout, restarting camera');
          await stopStream();
          // small cooldown helps iOS
          await new Promise(r => setTimeout(r, 150));
          return void startCameraAndDetection();
        }

        // 5) Auto-recover if the track dies after suspend/rotate
        const track = stream.getVideoTracks()[0];
        track.addEventListener('ended', () => {
          logger.warn('[StartScreen] Track ended, restarting camera');
          startCameraAndDetection();
        });

        // 6) Start your detection loop (unchanged, but use your refs)
        if (startDelayRef.current !== undefined) window.clearTimeout(startDelayRef.current);
        startDelayRef.current = window.setTimeout(() => {
          logger.info('[StartScreen] Starting detection loop...');
          if (detectIntervalRef.current !== undefined) window.clearInterval(detectIntervalRef.current);
          const token = runTokenRef.current;
          detectIntervalRef.current = window.setInterval(() => {
            captureAndSendFrame(token);
          }, 500);
        }, 800);

        // 7) Start a watchdog: if frames stop (0×0) or track not live → restart
        if (watchdogRef.current !== undefined) window.clearInterval(watchdogRef.current);
        watchdogRef.current = window.setInterval(() => {
          const s = (videoRef.current?.srcObject as MediaStream | null) ?? null;
          const t = s?.getVideoTracks?.()[0];
          const dead = !s || !t || t.readyState !== 'live';
          const zero = !videoRef.current || videoRef.current.videoWidth === 0 || videoRef.current.videoHeight === 0;
          if (dead || zero) {
            logger.warn('[StartScreen] Watchdog detected dead/zero stream; restarting');
            startCameraAndDetection();
          }
        }, 2000);
      } catch (err) {
        logger.error('[StartScreen] Camera access error:', err);
      } finally {
        startingRef.current = false;            // release lock even on errors
      }
    };

  useLayoutEffect(() => {
    if (visible) {
      setSelectedLang(Language.DE);
      i18n.changeLanguage(Language.DE);
    }
  }, [visible, i18n]);

  useEffect(() => {
    const els = [welcomeAudioDeRef.current, welcomeAudioEnRef.current].filter(Boolean) as HTMLAudioElement[];

    const onPlay = () => { isWelcomePlayingRef.current = true; };
    const onEnded = () => { isWelcomePlayingRef.current = false; };

    els.forEach(el => {
      el.addEventListener('play', onPlay);
      el.addEventListener('ended', onEnded);
      el.addEventListener('pause', onEnded);
    });

    return () => {
      els.forEach(el => {
        el.removeEventListener('play', onPlay);
        el.removeEventListener('ended', onEnded);
        el.removeEventListener('pause', onEnded);
      });
    };
  }, []);

  useEffect(() => {
    let done = false;

    const globalUnlock = async () => {
      if (done) return;
      done = true;

      if (!hasPrimedRef.current) {
        const els = [welcomeAudioDeRef.current, welcomeAudioEnRef.current].filter(Boolean) as HTMLAudioElement[];

        for (const el of els) {
          try {
            // Do NOT interrupt if it’s already playing
            if (!el.paused) continue;

            el.muted = true;
            // do not set currentTime=0 here — not needed for priming
            await el.play();      // allowed due to gesture
            el.pause();
            el.muted = false;
          } catch {}
        }
        hasPrimedRef.current = true;
      }

      try {
        // one shared AC for the app
        window.__appAC ??= new (window.AudioContext || (window as any).webkitAudioContext)();
        if (window.__appAC.state !== 'running') {
          await window.__appAC.resume();
        }

        // warm output with silent buffer (no blip)
        try {
          const ac = window.__appAC;
          const silent = ac.createBuffer(1, 1, ac.sampleRate);
          const node = ac.createBufferSource();
          node.buffer = silent;
          node.connect(ac.destination);
          node.start(0);
        } catch {}

        setAudioUnlocked(true);
        // if you also mirror into a ref: audioUnlockedRef.current = true;
      } finally {
        window.removeEventListener('pointerdown', globalUnlock, true);
        window.removeEventListener('touchstart', globalUnlock, true);
        window.removeEventListener('click', globalUnlock, true);
        window.removeEventListener('keydown', globalUnlock, true);
      }
    };

    window.addEventListener('pointerdown', globalUnlock, true);
    window.addEventListener('touchstart', globalUnlock, true);
    window.addEventListener('click', globalUnlock, true);
    window.addEventListener('keydown', globalUnlock, true);

    return () => {
      window.removeEventListener('pointerdown', globalUnlock, true);
      window.removeEventListener('touchstart', globalUnlock, true);
      window.removeEventListener('click', globalUnlock, true);
      window.removeEventListener('keydown', globalUnlock, true);
    };
  }, [setAudioUnlocked]);

  useEffect(() => {
    // wherever setAudioUnlocked(true) is called
    // also mirror it into a ref so captureAndSendFrame can read it synchronously
    const unsub = () => {}; // if you have context, mirror it
    return unsub;
  }, []);

  useEffect(() => {
    // Invalidate any pending async work from the previous run
    runTokenRef.current++;

    // If we’re hiding: abort + stop audio + cleanup timers/stream
    if (!visible) {
      detectAbortRef.current?.abort();
      detectAbortRef.current = null;

      for (const a of [welcomeAudioDeRef.current, welcomeAudioEnRef.current]) {
        try { a?.pause(); if (a) a.currentTime = 0; } catch {}
      }

      if (startDelayRef.current !== undefined) window.clearTimeout(startDelayRef.current);
      if (detectIntervalRef.current !== undefined) window.clearInterval(detectIntervalRef.current);
      if (watchdogRef.current !== undefined) window.clearInterval(watchdogRef.current);
      void stopStream();
      return;
    }

    // We’re (re)showing: new AbortController and start
    detectAbortRef.current = new AbortController();
    startCameraAndDetection();

    // Cleanup when visibility flips or component unmounts
    return () => {
      runTokenRef.current++;                 // invalidate anything still pending
      detectAbortRef.current?.abort();
      detectAbortRef.current = null;
    };
  }, [visible]);

  useEffect(() => {
    welcomeAudioDeRef.current = new Audio(welcomeAudioDe);
    welcomeAudioEnRef.current = new Audio(welcomeAudioEn);
    for (const el of [welcomeAudioDeRef.current, welcomeAudioEnRef.current]) {
      if (!el) continue;
      el.preload = 'auto';
    }
    return () => {
      for (const el of [welcomeAudioDeRef.current, welcomeAudioEnRef.current]) {
        try { el?.pause(); } catch {}
      }
      welcomeAudioDeRef.current = null;
      welcomeAudioEnRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (visible) {
      hasWelcomedRef.current = false;
    }
  }, [visible]);

  const playSequential = async (
    first?: HTMLAudioElement | null,
    second?: HTMLAudioElement | null,
    token = runTokenRef.current
  ) => {
    const shouldContinue = () =>
      token === runTokenRef.current &&
      visibleRef.current === true &&
      !(detectAbortRef.current?.signal.aborted);

    const playOnce = (audio: HTMLAudioElement) => new Promise<void>((resolve) => {
      if (!audio || !shouldContinue()) return resolve();

      const cleanup = () => {
        audio.removeEventListener('ended', onEnded);
        audio.removeEventListener('error', onError);
      };
      const onEnded = () => { cleanup(); resolve(); };
      const onError = () => { cleanup(); resolve(); };

      try {
        audio.pause();
        audio.currentTime = 0;
        const p = audio.play();
        if (p?.catch) p.catch(() => {}); // avoid unhandled rejection noise
      } catch {}

      audio.addEventListener('ended', onEnded, { once: true });
      audio.addEventListener('error', onError, { once: true });
    });

    if (first && shouldContinue()) await playOnce(first);
    if (second && shouldContinue()) await playOnce(second);
  };

  const resumeIfNeeded = () => {
    if (document.visibilityState !== 'visible') return;

    const vid = videoRef.current;
    const stream = (vid?.srcObject as MediaStream | null) ?? null;
    const track = stream?.getVideoTracks?.()[0];

    const needsRestart =
      !vid ||
      !stream ||
      !track ||
      track.readyState === 'ended' ||
      !vid.videoWidth ||
      !vid.videoHeight;

    if (needsRestart) {
      logger.info('[StartScreen] Resume detected, restarting camera');
      // Re-run your existing boot function
      startCameraAndDetection?.();
    } else {
      // Sometimes iOS leaves the video paused; nudge it
      vid.play().catch(() => {});
    }
  };

  useEffect(() => {
    const nudge = () => videoRef.current?.play().catch(() => {});
    document.addEventListener('touchstart', nudge, { passive: true });
    document.addEventListener('click', nudge);
    return () => {
      document.removeEventListener('touchstart', nudge);
      document.removeEventListener('click', nudge);
    };
  }, []);

  useEffect(() => {
    if (!visible) return;

    const onPageShow = () => setTimeout(resumeIfNeeded, 50);
    const onVisibility = resumeIfNeeded;
    const onOrientation = resumeIfNeeded;

    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('orientationchange', onOrientation);

    return () => {
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('orientationchange', onOrientation);
    };
  }, [visible]);

  const handleStartClick = async () => {
    // Ignore any stale click / synthetic event if we're not visible anymore
    if (!visibleRef.current) {
      logger.debug('[StartScreen] handleStartClick ignored because not visible');
      return;
    }

    // Immediately invalidate this run so any pending callbacks become no-ops
    runTokenRef.current++;

    // Abort any in-flight detection call and clear timers
    detectAbortRef.current?.abort();
    detectAbortRef.current = null;
    if (startDelayRef.current !== undefined) window.clearTimeout(startDelayRef.current);
    if (detectIntervalRef.current !== undefined) window.clearInterval(detectIntervalRef.current);
    if (watchdogRef.current !== undefined) window.clearInterval(watchdogRef.current);

    // Stop the camera right away
    await stopStream();

    // Hard stop any StartScreen audios already playing
    for (const a of [welcomeAudioDeRef.current, welcomeAudioEnRef.current]) {
      try { a?.pause(); if (a) a.currentTime = 0; } catch {}
    }

    // (now proceed with your existing logic)
    await i18n.changeLanguage(selectedLang);
    try {
      await updateSettings(
        selectedLang,
        false,
        'Valtra Q-Series',
        'automatic',
        true,
        true,
        null
      );
    } catch (err) {
      logger.error('Failed to update settings:', err);
    }

    await refetchSession(selectedLang);

    // Keep this too (it’s harmless now, just ensures)
    welcomeAudioDeRef.current?.pause();
    welcomeAudioEnRef.current?.pause();
    if (welcomeAudioDeRef.current) welcomeAudioDeRef.current.currentTime = 0;
    if (welcomeAudioEnRef.current) welcomeAudioEnRef.current.currentTime = 0;

    onStart(selectedLang);
  };

  const stopStream = async () => {
    if (videoRef.current?.srcObject) {
      (videoRef.current.srcObject as MediaStream)
        .getTracks()
        .forEach((track) => track.stop());
      videoRef.current.srcObject = null;
      // tiny cooldown for iOS
      await new Promise((r) => setTimeout(r, 120));
    }
  };

  const handleSelectLanguage = (lang: Language) => {
    setSelectedLang(lang);
    i18n.changeLanguage(lang);
  };

  const handleLogoClick = () => {
    const elem = document.documentElement; // or any specific container

    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if ((elem as any).webkitRequestFullscreen) { // Safari
      (elem as any).webkitRequestFullscreen();
    } else if ((elem as any).msRequestFullscreen) { // IE11
      (elem as any).msRequestFullscreen();
    }
  };

  const captureAndSendFrame = async (token = runTokenRef.current) => {
    if (!visibleRef.current || token !== runTokenRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || hasWelcomedRef.current) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    // Avoid work if no real frames
    if (video.videoWidth === 0 || video.videoHeight === 0) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(async (blob) => {
      if (!blob) return;
      if (!visibleRef.current || token !== runTokenRef.current) return;
      if (hasWelcomedRef.current) return;

      hasWelcomedRef.current = true;

      try {
        // If your API supports AbortSignal, thread it here as a second arg
        // const result = await sendHumanDetectionImage(blob, { signal: detectAbortRef.current?.signal });
        const result = await sendHumanDetectionImage(blob);

        // We might have been hidden while awaiting the server
        if (!visibleRef.current || token !== runTokenRef.current || detectAbortRef.current?.signal.aborted) {
          hasWelcomedRef.current = false;
          return;
        }

        if (result.human_detected) {
          // stop anything currently playing
          for (const a of [welcomeAudioDeRef.current, welcomeAudioEnRef.current]) {
            try { a?.pause(); if (a) a.currentTime = 0; } catch {}
          }

          await playSequential(welcomeAudioDeRef.current, welcomeAudioEnRef.current, token);

          // Allow another trigger after cooldown, but only if this run is still active & visible
          const myToken = token;
          setTimeout(() => {
            if (myToken === runTokenRef.current && visibleRef.current) {
              hasWelcomedRef.current = false;
            }
          }, 120000);
        } else {
          hasWelcomedRef.current = false;
        }
      } catch (error) {
        logger.error('Failed to detect human:', error);
        hasWelcomedRef.current = false; // allow retry on error
      }
    }, 'image/jpeg', 0.8);
  };

  if (!visible) return null;

  return (
    <div className={styles.modalFullscreen}>

      {/* Grid with 40px gap containing: coach logo, static image, languages, start button */}
      <div className={styles.modalContentGrid}>

        <img src={valtraLogo} className={styles.clientLogo} alt="Valtra" />
        <img src={valtraCoach} className={styles.coachLogo} alt="Valtra Coach" />

        {/* 2) Static hero image */}
        <img src={heroImage} className={styles.staticImage} alt="" />

        {/* 3) Language buttons */}
        <div>
          <div className={styles.selectLanguageText}>{t("start.selectLanguage")}</div>
          <div className={styles.languageGrid}>
            {languages.map((lang) => (
              <div
                key={lang.code}
                className={`${styles.languageOption} ${selectedLang === lang.code ? styles.selected : ''}`}
                onClick={() => handleSelectLanguage(lang.code)}
              >
                <span>{lang.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* 4) Start button */}
        <div className={styles.startRow}>
          <button className={styles.startButton} onClick={handleStartClick}>
            <span className={styles.startButtonText}>
              {t ? t('start', 'START') : 'START'}
            </span>
          </button>
        </div>

        {/* 5) Privacy */}
        <div className={styles.privacyPolicyAndQRCodeContainer}>
          <div className={styles.privacyPolicyDesktop}>
            <p>{t("start.privacy.desktop.1")}</p>
            <p>{t("start.privacy.desktop.2")}</p>
            <p>{t("start.privacy.desktop.3")}</p>
            <p>{t("start.privacy.desktop.4")}</p>
            <p>{t("start.privacy.desktop.5")}</p>
          </div>
          <div className={styles.privacyPolicyMobile}>
            <p>{t("start.privacy.mobile.1")}</p>
            <p>{t("start.privacy.mobile.2")}</p>
          </div>
          <div className={styles.qrCode}>
            <p className={styles.qrTitle}>{t("start.privacy.qrTitle")}</p>
            <img className={styles.qrImage} src={qrCode} alt={t("start.privacy.qrAlt")} />
          </div>
        </div>
      </div>

      {/* Footer stays outside the grid */}
      <div className={styles.poweredBy}>
        <span className={styles.poweredText}>Powered by</span>
        <img src={spogenLogo} className={styles.spogenLogo} alt="Spogen Logo" onClick={handleLogoClick} />
      </div>

      {/* Hidden camera + canvas for detection */}
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        // keep it rendered: tiny, non-interactive
        style={{
          position: 'absolute',
          bottom: 15,
          width: 20,
          height: 20,
          opacity: 0,
          pointerEvents: 'none',
        }}
      />
      <canvas
        ref={canvasRef}
        style={{ position:'absolute', bottom: 15, width:20, height:20, opacity:0, pointerEvents:'none' }}
      />
    </div>
  );
};

export default StartScreen;
